import React, { useState, useEffect } from 'react'
import Select from 'react-select'
import { KTSVG } from '../../../../_metronic/helpers'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import {
  useGetAllUsersOfAccountQuery,
  InvestorRoles,
  useUpdateRoleOfInvestorMutation,
  useAddInvestorToUserMutation,
  useAddMultipleInvestorsToUserMutation,
  useAddMultipleInvestorToMultipleUserMutation,
  useGetAllFundsOfUserQuery,
  RoleName,
} from '../../../../generated/graphql'
import { roles } from '../utils'
import { selectCustomStyles } from '../../../../utils/Select'
import Toast, { ToastType } from '../../../../utils/toast'

interface UpdateUserRolePopupProps {
  show: boolean
  onHide: () => void
  userId: string
  userName: string
  currentRole?: string | null
  isAccountAdmin?: boolean
}

const UpdateUserRolePopup: React.FC<UpdateUserRolePopupProps> = ({
  show,
  onHide,
  userId,
  userName,
  currentRole,
  isAccountAdmin: initialIsAccountAdmin = false,
}) => {
  const [selectedRole, setSelectedRole] = useState<RoleName | null>(null)
  const [isAccountAdmin, setIsAccountAdmin] = useState<boolean>(false)

  const { mutate: updateRoleOfInvestor, isLoading: loading } = useUpdateRoleOfInvestorMutation(
    graphqlRequestClient,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(useGetAllUsersOfAccountQuery.getKey())
        queryClient.invalidateQueries(useGetAllFundsOfUserQuery.getKey())
        Toast(
          // `${userName || 'User'}'s role has been updated to ${selectedRole || 'role'} successfully!`,
          `${userName || 'User'}'s role has been updated successfully!`,

          ToastType.success
        )
        setSelectedRole(null)
        onHide()
      },
      onError: (error: Error) => {
        Toast(error.message || 'Failed to update role. Please try again.', ToastType.error)
      },
    }
  )

  const roleOptions = roles
  useEffect(() => {
    if (show) {
      const currentRoleData = roles.find((role) => role.label === currentRole)
      const roleValue = (currentRoleData?.value as RoleName) || null
      setSelectedRole(roleValue)
      setIsAccountAdmin(initialIsAccountAdmin)
    }
  }, [show, currentRole, initialIsAccountAdmin])

  const handleRoleChange = (selectedOption: any) => {
    const selected = selectedOption ? (selectedOption.value as RoleName) : null
    setSelectedRole(selected)

    if (selected === 'ADMIN' || selected === 'ADMIN_DYNAMIC_TABLE') {
      setIsAccountAdmin(true)
    } else {
      setIsAccountAdmin(false)
    }
  }

  const handleSubmit = () => {
    if (!selectedRole || !userId) {
      Toast('Please select a role', ToastType.error)
      return
    }

    updateRoleOfInvestor({
      input: {
        userId: Number(userId),
        role: selectedRole,
        isAccountAdmin,
      },
    })
    queryClient.invalidateQueries(useUpdateRoleOfInvestorMutation.getKey())
  }

  if (!show) return null

  return (
    <div
      className='modal fade show d-block'
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className='modal-dialog modal-dialog-centered' style={{ maxWidth: '500px' }}>
        <div className='modal-content'>
          <div className='modal-header'>
            <div className='col d-flex justify-content-between px-1'>
              <div className='py-3'>
                <h3 className='fw-bold text-dark'>Update Role for {userName || 'User'}</h3>
              </div>
              <button
                className='btn btn-sm btn-icon btn-active-light-primary mt-0'
                type='button'
                data-bs-toggle='tooltip'
                title='Close'
                onClick={onHide}
                disabled={loading}
              >
                <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
              </button>
            </div>
          </div>

          <div className='modal-body'>
            <div className='mb-5'>
              <label className='form-label fw-semibold required'>
                Select New Role for Investors/Funds
              </label>
              <Select
                options={roleOptions}
                styles={selectCustomStyles}
                name='roleType'
                onChange={handleRoleChange}
                placeholder='Select a role...'
                value={roleOptions.find((option) => option.value === selectedRole) || null}
                isClearable
                isDisabled={loading}
              />
            </div>

            <div className='d-flex justify-content-end gap-3 mt-4'>
              <button type='button' className='btn btn-light' onClick={onHide} disabled={loading}>
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleSubmit}
                disabled={loading || !selectedRole}
              >
                {loading ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' />
                    Updating...
                  </>
                ) : (
                  'Update Role'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UpdateUserRolePopup
